import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Container,
  Stack,
  Button,
} from '@mui/material';
import { CheckCircle, CreditCard } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchCurrentUser } from '../userSlice';
import SubscriptionSetup from './SubscriptionSetup';
import onboardingService from '../services/onboardingService';
import { getOrganizationDetails } from '../services/organizationService';

// Helper function to calculate pricing based on room count
// For onboarding, we always start with the base plan of $9 for 3 rooms
const calculatePrice = (roomCount) => {
  // During onboarding, always start with base plan
  return 9;
};

export default function OnboardingBillingStep() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [hasExistingSubscription, setHasExistingSubscription] = useState(false);
  const [continueLoading, setContinueLoading] = useState(false);
  const [error, setError] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [onboardingData, orgData] = await Promise.all([
          onboardingService.getOnboardingStatus(),
          getOrganizationDetails()
        ]);
        
        setOnboardingStatus(onboardingData);
        setOrganization(orgData);
        
        // Check if organization has an active subscription
        const hasActiveSubscription = orgData?.subscription?.isActive && orgData?.subscription?.subscriptionId;
        setHasExistingSubscription(hasActiveSubscription);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubscriptionSuccess = async (subscription) => {
    try {
      // Mark billing step as completed
      await onboardingService.updateOnboardingStep('billing', 'complete');
      
      // Refresh user data to get updated onboarding status
      await dispatch(fetchCurrentUser());
      
      // Navigate to next step
      navigate('/onboarding/complete');
    } catch (error) {
      console.error('Error completing billing step:', error);
      setError('Failed to complete billing setup');
    }
  };

  const handleContinueWithExistingSubscription = async () => {
    setContinueLoading(true);
    setError('');

    try {
      // Mark billing step as completed
      await onboardingService.updateOnboardingStep('billing', 'complete');
      
      // Refresh user data to get updated onboarding status
      await dispatch(fetchCurrentUser());
      
      // Navigate to next step
      navigate('/onboarding/complete');
    } catch (error) {
      console.error('Error completing billing step:', error);
      setError('Failed to continue');
    } finally {
      setContinueLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography>Loading organization data...</Typography>
        </Box>
      </Container>
    );
  }

  // Data for stepper - matching first stage design
  const steps = [
    { label: "Basic info", active: false, completed: true },
    { label: "Billing", active: true, completed: false },
    { label: "Complete", active: false, completed: false },
  ];

  const currentRoomCount = organization?.subscription?.currentRoomCount || 0;
  const monthlyPrice = calculatePrice(currentRoomCount);

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
      <Stack spacing={10} alignItems="center">
        {/* Header Section */}
        <Stack spacing={6} alignItems="center" sx={{ width: "100%" }}>
          <Stack spacing={2} alignItems="center">
            <Typography
              variant="h3"
              sx={{
                fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                fontWeight: 500,
                color: 'text.primary',
                textAlign: 'center',
                lineHeight: { xs: '46px', sm: '54px', md: '65px' },
                fontSize: { xs: '2rem', sm: '2.25rem', md: '3rem' }
              }}
            >
              {hasExistingSubscription ? "You're all set!" : "Choose your plan"}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                fontWeight: 500,
                color: 'text.secondary',
                textAlign: 'center',
                maxWidth: '538px'
              }}
            >
              {hasExistingSubscription 
                ? "Your subscription is active and ready to use." 
                : "Simple, transparent pricing. Start with 3 rooms for just $9/month."
              }
            </Typography>
          </Stack>

          {/* Progress Stepper */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: "100%", maxWidth: 600, gap: 0 }}
          >
            {steps.map((step, index) => (
              <Stack key={index} alignItems="center" spacing={1}>
                <Paper
                  elevation={0}
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 49 },
                    borderRadius: 24,
                    backgroundColor: step.completed 
                      ? 'primary.main' 
                      : step.active 
                        ? 'primary.light' 
                        : 'grey.300',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {step.completed && (
                    <CheckCircle sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
                  )}
                </Paper>
                <Typography
                  sx={{
                    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                    fontWeight: step.active || step.completed ? 500 : 400,
                    color: step.active || step.completed ? 'text.primary' : 'text.disabled',
                    textAlign: 'center',
                    fontSize: { xs: '0.9rem', sm: '1.25rem' }
                  }}
                  variant="h6"
                >
                  {step.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>

        {/* Content Section */}
        <Stack spacing={6} sx={{ width: "100%", maxWidth: 900 }}>
          {error && (
            <Alert severity="error" sx={{ width: "100%" }}>
              {error}
            </Alert>
          )}

          {hasExistingSubscription ? (
            // Existing Subscription Content
            <Paper 
              sx={{ 
                p: { xs: 2, sm: 4 }, 
                width: "100%", 
                maxWidth: 500, 
                mx: 'auto',
                textAlign: 'center',
                border: '2px solid',
                borderColor: 'success.light',
                backgroundColor: 'success.50'
              }}
            >
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Subscription Active
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                You have {currentRoomCount} room{currentRoomCount !== 1 ? 's' : ''} 
                {' '}at ${monthlyPrice}/month
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleContinueWithExistingSubscription}
                disabled={continueLoading}
                sx={{
                  width: { xs: '100%', sm: 300 },
                  height: 60,
                  backgroundColor: 'primary.main',
                  borderRadius: 1,
                  textTransform: "none",
                  boxShadow: "0px 13px 9.7px -8px rgba(32, 141, 244, 0.24)",
                  fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                  fontWeight: 600,
                  fontSize: 16,
                }}
              >
                {continueLoading ? 'Continuing...' : 'Complete setup'}
              </Button>
            </Paper>
          ) : (
            // New Subscription Content
            <>
              {/* Pricing and Benefits - Responsive layout */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
                <Box sx={{ display: 'flex', gap: { xs: 3, md: 6 }, alignItems: { xs: 'flex-start', md: 'center' }, maxWidth: 800, flexDirection: { xs: 'column', md: 'row' }, width: '100%' }}>
                  {/* Pricing Section */}
                  <Box sx={{ textAlign: { xs: 'left', md: 'center' }, width: { xs: '100%', md: 'auto' } }}>
                    <CreditCard sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h2" sx={{ mb: 1, fontWeight: 700, color: 'primary.main' }}>
                      $9
                      <Typography component="span" variant="h4" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        /month
                      </Typography>
                    </Typography>
                    
                    <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, mb: 2 }}>
                      For up to 3 rooms
                    </Typography>
                    
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      Additional rooms: $3/month each
                    </Typography>
                  </Box>

                  {/* Benefits Section */}
                  <Box sx={{ flex: 1, width: '100%' }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                      What's included:
                    </Typography>
                    <Stack spacing={2}>
                      {[
                        'Unlimited bookings per room',
                        'Team member collaboration',
                        'Calendar integrations (Google, Outlook)',
                        'Email notifications & reminders',
                        'Advanced reporting & analytics',
                        '24/7 customer support'
                      ].map((feature, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
                          <Typography 
                            variant="body1"
                            sx={{
                              fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                              color: 'text.primary'
                            }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </Box>

              {/* Payment Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Payment Information
                </Typography>
                
                {organization && (
                  <SubscriptionSetup 
                    organizationId={organization._id}
                    onSuccess={handleSubscriptionSuccess}
                  />
                )}
                
                {/* Help Text */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif"
                    }}
                  >
                    🔒 Secure payments by Stripe • Cancel anytime • No setup fees
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Stack>
      </Stack>
    </Container>
  );
} 